import { IconButton, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { IDefaultFailureType } from "entities/FailureDetailsEntity/FailureDetailsEntityInterfaces";
import { IEventReportDetails } from "entities/EventReports/EventReportsEntityInterfaces";
import {
    AreaField,
    FailureCodeField,
    FailureTypeField,
    FunctionalLocationField,
    ISegmentFieldValues,
    NotesField,
    OperatorField,
    applyAreaChange,
    applyFailureCodeChange,
    applyFailureTypeChange,
    applyFunctionalLocationChange,
    applyNotesChange,
    applyOperatorChange,
} from "../SegmentFields";
import { ISplitSegmentState } from "./SplitDowntimeModalInterfaces";
import { useStyles } from "./SplitDowntimeModal.styles";

interface ISplitSegmentRowProps {
    segment: ISplitSegmentState;
    index: number;
    row: IEventReportDetails;
    defaultFailureTypes: IDefaultFailureType[];
    millTimezone?: string;
    minStartIso: string;
    maxStartIso: string;
    onFieldsChange: (key: string, fields: ISegmentFieldValues) => void;
    onStartChange: (key: string, iso: string | null) => void;
    onRemove: (key: string) => void;
}

export const SplitSegmentRow = ({
    segment,
    index,
    row,
    defaultFailureTypes,
    millTimezone,
    minStartIso,
    maxStartIso,
    onFieldsChange,
    onStartChange,
    onRemove,
}: ISplitSegmentRowProps) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const tz = millTimezone || "UTC";
    const { fields } = segment;
    const machineCenterId = row.machine_center_id;
    const placeholder = t("locations.mill_dashboards.details.split.select_placeholder");

    const toPicker = (iso: string | null): Dayjs | null =>
        iso ? dayjs(moment.utc(iso).tz(tz).format("YYYY-MM-DDTHH:mm:ss")) : null;
    const fromPicker = (value: Dayjs | null): string | null =>
        value && value.isValid() ? moment.tz(value.format("YYYY-MM-DDTHH:mm:ss"), tz).utc().toISOString() : null;

    const update = (next: ISegmentFieldValues) => onFieldsChange(segment.key, next);

    const fieldLabel = (text: string, required = false) => (
        <Typography className={classes.formLabel}>{`${text}${required ? " *" : ""}`}</Typography>
    );

    return (
        <div className={classes.segmentCard}>
            <div className={classes.segmentHeader}>
                <Typography className={classes.segmentTitle}>
                    {segment.isOriginal
                        ? t("locations.mill_dashboards.details.split.original_segment_label")
                        : t("locations.mill_dashboards.details.split.added_segment_label", { index })}
                </Typography>
                {!segment.isOriginal && (
                    <IconButton
                        size="small"
                        className={classes.removeButton}
                        aria-label={t("locations.mill_dashboards.details.split.remove_segment")}
                        onClick={() => onRemove(segment.key)}
                    >
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                )}
            </div>

            <div className={classes.field}>
                {fieldLabel(t("locations.mill_dashboards.details.split.segment_start"), !segment.isOriginal)}
                {segment.isOriginal ? (
                    <Typography className={classes.fixedStart}>
                        {moment.utc(segment.start as string).tz(tz).format("MMM D, YYYY h:mm A")}
                    </Typography>
                ) : (
                    <DateTimePicker
                        value={toPicker(segment.start)}
                        minDateTime={toPicker(minStartIso) ?? undefined}
                        maxDateTime={toPicker(maxStartIso) ?? undefined}
                        onChange={(value) => onStartChange(segment.key, fromPicker(value as Dayjs | null))}
                        ampm
                        slotProps={{ textField: { fullWidth: true, className: classes.dateTimePicker } }}
                    />
                )}
            </div>

            <div className={classes.fieldGrid}>
                <div className={classes.field}>
                    {fieldLabel(t("locations.mill_dashboards.details.failure_type"), true)}
                    <FailureTypeField
                        variant="modal"
                        placeholder={placeholder}
                        machineCenterId={machineCenterId}
                        value={fields.failure_type_id}
                        onChange={(id, isLocationSpecific) => update(applyFailureTypeChange(fields, id, isLocationSpecific))}
                    />
                </div>

                {fields.is_location_specific && (
                    <>
                        <div className={classes.field}>
                            {fieldLabel(t("locations.mill_dashboards.details.area"), true)}
                            <AreaField
                                key={`area-${segment.key}-${fields.failure_type_id}`}
                                variant="modal"
                                placeholder={placeholder}
                                machineCenterId={machineCenterId}
                                failureTypeId={fields.failure_type_id}
                                value={fields.area_id}
                                onChange={(id) => update(applyAreaChange(fields, id))}
                            />
                        </div>
                        <div className={classes.field}>
                            {fieldLabel(t("locations.mill_dashboards.details.functional_location"), true)}
                            <FunctionalLocationField
                                key={`fl-${segment.key}-${fields.area_id}`}
                                variant="modal"
                                placeholder={placeholder}
                                areaId={fields.area_id}
                                failureTypeId={fields.failure_type_id}
                                value={fields.functional_location_id}
                                onChange={(id) => update(applyFunctionalLocationChange(fields, id))}
                            />
                        </div>
                    </>
                )}

                <div className={classes.field}>
                    {fieldLabel(t("locations.mill_dashboards.details.failure_code"), true)}
                    <FailureCodeField
                        key={`code-${segment.key}-${fields.failure_type_id}-${fields.functional_location_id}`}
                        variant="modal"
                        placeholder={placeholder}
                        machineCenterId={machineCenterId}
                        failureTypeId={fields.failure_type_id}
                        functionalLocationId={fields.functional_location_id}
                        locationSpecific={fields.is_location_specific}
                        defaultFailureTypes={defaultFailureTypes}
                        value={fields.failure_code_id}
                        onChange={(id) => update(applyFailureCodeChange(fields, id))}
                    />
                </div>

                <div className={classes.field}>
                    {fieldLabel(t("locations.mill_dashboards.details.operator"))}
                    <OperatorField
                        variant="modal"
                        placeholder={placeholder}
                        machineCenterId={machineCenterId}
                        clusterId={row.cluster_id}
                        value={fields.user_id}
                        onChange={(id) => update(applyOperatorChange(fields, id))}
                    />
                </div>

                <div className={classes.field}>
                    {fieldLabel(t("locations.mill_dashboards.details.comments"))}
                    <NotesField
                        variant="outlined"
                        value={fields.notes ?? ""}
                        onChange={(value) => update(applyNotesChange(fields, value))}
                    />
                </div>
            </div>
        </div>
    );
};
